import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import SignatureCanvas from 'react-signature-canvas';

import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import Button from 'modules/views/shared/Button';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import {
  uploadSignatureToS3,
  submitConsentAcceptances,
  setOnboarding,
  fetchWorkerDetails,
  fetchWorkerConsentAcceptances,
} from 'modules/state-mgmt/onboarding';
import { createGuid, prettifyErrorResponse, getClientIpAddress } from 'utils';
import { getImageFormat } from 'utils/files';

import { useStyles } from './styles';

const SUMMARY_STATEMENTS = ['statement1', 'statement2', 'statement3', 'statement4'];

const RegistrationSignature = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { goToNextStep, goToPreviousStep } = useWizardStepNavigation();

  const signPadRef = useRef<any>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { workerId, languageCode, projectId, consentAgreementContentIds, isOffline, isWorker, uploadingDetails, temp } =
    useAppSelector(state => ({
      workerId: state.onboarding.id,
      languageCode: state.app.languageCode,
      projectId: state.onboarding.temp?.project?.value,
      consentAgreementContentIds: state.onboarding.temp?.consentAcceptances || [],
      isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
      isWorker: !state.user?.userId || state.user?.isWorker,
      uploadingDetails: state.onboarding.uploadingDetails,
      temp: state.onboarding.temp,
    }));

  // Warm the public-IP lookup while the worker reads/draws, so it's cached (best-effort)
  // by the time the consent acceptances are submitted.
  useEffect(() => {
    !isOffline && getClientIpAddress();
  }, [isOffline]);

  // Keep the canvas backing store in sync with its on-screen (CSS) size. A <canvas>
  // has two sizes: the CSS display size and the drawing-buffer size. react-signature-canvas
  // matches them only once, in componentDidMount, reading offsetWidth/offsetHeight at that
  // instant. When this wizard step mounts before its layout has settled (width/height still 0
  // during a route/step transition), the buffer is created empty and the canvas cannot be
  // drawn on until the next window resize — which is the intermittent "can't draw" bug.
  // We re-size it ourselves whenever the element's box changes (including 0 → real size),
  // preserving any strokes across the resize.
  const resizeCanvas = useCallback(() => {
    const canvas = signPadRef.current?.getCanvas();
    if (!canvas) return;
    const { offsetWidth, offsetHeight } = canvas;
    if (!offsetWidth || !offsetHeight) return; // not laid out yet — the observer will re-fire
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const targetWidth = offsetWidth * ratio;
    const targetHeight = offsetHeight * ratio;
    // Already correctly sized → skip (avoids clearing strokes on no-op observer fires).
    if (canvas.width === targetWidth && canvas.height === targetHeight) return;
    const previousData = signPadRef.current.isEmpty() ? null : signPadRef.current.toData();
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    canvas.getContext('2d')?.scale(ratio, ratio);
    signPadRef.current.clear();
    if (previousData) signPadRef.current.fromData(previousData);
  }, []);

  useEffect(() => {
    const canvas = signPadRef.current?.getCanvas();
    if (!canvas || typeof ResizeObserver === 'undefined') {
      resizeCanvas();
      return undefined;
    }
    const observer = new ResizeObserver(() => resizeCanvas());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [resizeCanvas]);

  const onClear = () => {
    signPadRef.current?.clear();
    setHasSignature(false);
  };

  const onSubmit = async () => {
    if (!signPadRef.current || signPadRef.current.isEmpty()) {
      return;
    }

    const signData = signPadRef.current.getTrimmedCanvas().toDataURL('image/png');

    if (isOffline && !isWorker) {
      dispatch(
        setOnboarding({
          // Mark the signature as captured so every `signatureUrl`-based check
          // (consent route guard, step-completion, TakePhoto back button) treats the
          // consent flow as done and lets the worker advance. We store the drawn
          // image as a stand-in URL — the background uploader replaces it with the
          // real S3 URL once it re-fetches worker details after sync. The actual
          // upload still runs from `temp.signature` via `pendingUploads.signature`.
          signatureUrl: signData,
          temp: { ...(temp || {}), signature: signData },
          uploadingDetails: {
            ...(uploadingDetails || {}),
            isOffline: true,
            pendingUploads: {
              ...(uploadingDetails?.pendingUploads || {}),
              signature: true,
              consentAcceptances: true,
            },
          },
        }),
      );
      goToNextStep();
    } else {
      try {
        setIsLoading(true);
        const fileName = `${createGuid()}.${getImageFormat(signData)}`;
        // 1) Upload + confirm the signature (CH-3972). 2) Persist all consent
        // acceptances in a single call, only after the signature succeeds (CH-3968).
        const uploadResult: any = await dispatch(
          uploadSignatureToS3({ workerId, languageCode, signData, fileName }),
        ).unwrap();
        await dispatch(
          submitConsentAcceptances({
            workerId,
            projectId,
            languageCode,
            consentAgreementContentIds,
            source: 'WorkerOnboarding',
          }),
        ).unwrap();
        // Mark the signature as captured so the consent sub-steps are no longer
        // accessible and the worker proceeds. The authoritative URL is refreshed by
        // the worker-details fetch below; fall back to the drawn image URL meanwhile.
        // Also keep the drawn image in temp. It is the only signature the app can render offline —
        // the authoritative signatureUrl is a remote S3 URL that will not load without a connection,
        // so the project consent forms rely on this copy to show the Apply Signature preview.
        dispatch(
          setOnboarding({
            signatureUrl: uploadResult?.url || uploadResult?.signatureUrl || signData,
            temp: { ...(temp || {}), signature: signData },
          }),
        );
        // Refresh the worker and consent agreements so Redux reflects the persisted
        // signature and acceptance flags. Best-effort — the signature/consent are
        // already saved, so a failed refresh must not strand the worker here; we
        // still advance once both requests have settled.
        await Promise.allSettled([
          dispatch(fetchWorkerDetails({ workerId, languageCode })).unwrap(),
          dispatch(fetchWorkerConsentAcceptances({ projectId, workerId, languageCode })).unwrap(),
        ]);
        goToNextStep();
      } catch (error) {
        setIsLoading(false);
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      }
    }
  };

  const fields = useMemo(
    () => [
      {
        name: t('registrationSignature.title'),
        id: 'signature',
        isRequired: true,
      },
    ],
    [t],
  );

  const content = (
    <div className={classes.container}>
      <ContentHeader title={t('registrationSignature.title')} subtitle={t('registrationSignature.subtitle')} />

      <p className={classes.summaryHeading}>{t('registrationSignature.summary.heading')}</p>
      <ul className={classes.summaryList}>
        {SUMMARY_STATEMENTS.map(key => (
          <li key={key}>{t(`registrationSignature.summary.${key}`)}</li>
        ))}
      </ul>

      <div className={classes.canvasWrapper}>
        <SignatureCanvas
          penColor="black"
          ref={signPadRef}
          clearOnResize={false}
          canvasProps={{ className: classes.signPad }}
          onEnd={() => setHasSignature(true)}
        />
        {!hasSignature && <span className={classes.placeholder}>{t('registrationSignature.canvasPlaceholder')}</span>}
      </div>

      <div className={classes.clearContainer}>
        <Button
          label={t('registrationSignature.clear')}
          variant="text"
          disabled={!hasSignature || isLoading}
          styleClass={classes.clearButton}
          onClick={onClear}
        />
      </div>

      <p className={classes.disclaimer}>{t('registrationSignature.disclaimer')}</p>

      <NavigationButtons
        secondaryButtonProps={{
          label: t('registrationSignature.back'),
          onClick: () => goToPreviousStep(),
        }}
        primaryButtonProps={{
          label: t('registrationSignature.next'),
          hasEndArrow: true,
          isLoading,
          disabled: !hasSignature || isLoading,
          onClick: onSubmit,
        }}
      />
    </div>
  );

  return <WithWizard disableNavigation={isLoading} content={content} fields={fields} />;
};

export default memo(RegistrationSignature);
